import {
    ColumnDef,
    ColumnFiltersState,
    ColumnOrderState,
    ColumnSizingState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    RowPinningState,
    RowSelectionState,
    SortDirection,
    Table,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import { differenceInMinutes } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { Dropdown, DropdownButtonGroup } from 'widgets/Dropdown';
import { Portal } from 'widgets/Portal';
import { PageSpinner } from 'widgets/Spinner';

import { Select } from '../form';
import { IconChevron, IconDots, IconDoubleChevron } from '../icon';
import { HorizontalLayout, VerticalLayout } from '../Layout';
import { Text } from '../Text';
import {
    ArrowIconWrapper,
    DataTable,
    DataTableCell,
    DataTableRow,
    HeaderRow,
    PageNumber,
    PageNumberDots,
    ResizeHandle,
    RightIconChevron,
    SortArrow,
    StyledDataTableHeaderCell,
} from './DataTable';
import { exportTableToCsv } from './exportTableToCsv';

interface CustomTablePageProps<DataType extends object> {
    rowOnClick?: (object: DataType) => void;
    columns: ColumnDef<DataType, any>[];
    data: DataType[];
    defaultSortCol?: string;
    defaultSortDirection?: SortDirection;
    isLoading?: boolean;
    isUpdating?: boolean;
    exportName: string;
    /** Filters owned by the caller. Update this prop; table filter setters do not change them. */
    columnFilters?: ColumnFiltersState;
    initialColumnVisibility?: Record<string, boolean>;
    columnVisibility?: VisibilityState;
    onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
    columnSizing?: ColumnSizingState;
    onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
    columnOrder?: ColumnOrderState;
    onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
    onFilteredRowCountChange?: (count: number) => void;
    renderToolbar?: (table: Table<DataType>) => React.ReactNode;
    toolbarPosition?: 'overlay' | 'header';
    enableRowSelection?: boolean | ((row: DataType) => boolean);
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => void;
    /** Row ids (from getRowId) pinned to the top of the table regardless of sorting. */
    rowPinning?: RowPinningState;
    getRowId?: (row: DataType) => string;
    cardClassName?: string;
    exportPortalId?: string;
    getSubRows?: (originalRow: DataType, index: number) => DataType[] | undefined;
}

export const CustomTablePage = <T extends object>({
    rowOnClick,
    columns,
    data,
    defaultSortCol,
    defaultSortDirection = 'asc',
    isLoading,
    isUpdating,
    exportName,
    columnFilters,
    initialColumnVisibility,
    columnVisibility,
    onColumnVisibilityChange,
    columnSizing,
    onColumnSizingChange,
    columnOrder,
    onColumnOrderChange,
    onFilteredRowCountChange,
    renderToolbar,
    toolbarPosition = 'overlay',
    enableRowSelection,
    rowSelection,
    onRowSelectionChange,
    rowPinning,
    getRowId,
    cardClassName,
    exportPortalId,
    getSubRows,
}: CustomTablePageProps<T>) => {
    const LOCAL_STORAGE_KEY = `TABLE_SCROLL_POS_${exportName}`;

    const [searchParams] = useSearchParams();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const table = useReactTable({
        data,
        columns,
        getSubRows,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableRowSelection:
            typeof enableRowSelection === 'function' ? (row) => enableRowSelection(row.original) : enableRowSelection,
        enableRowPinning: rowPinning !== undefined,
        keepPinnedRows: true,
        getRowId: getRowId,
        columnResizeMode: onColumnSizingChange ? 'onChange' : undefined,
        state: {
            ...(columnFilters !== undefined ? { columnFilters } : {}),
            ...(enableRowSelection ? { rowSelection: rowSelection || {} } : {}),
            ...(columnVisibility !== undefined ? { columnVisibility } : {}),
            ...(columnSizing !== undefined ? { columnSizing } : {}),
            ...(columnOrder !== undefined ? { columnOrder } : {}),
            ...(rowPinning !== undefined ? { rowPinning } : {}),
        },
        onRowSelectionChange: enableRowSelection ? onRowSelectionChange : undefined,
        onColumnVisibilityChange,
        onColumnSizingChange,
        onColumnOrderChange,
        initialState: {
            pagination: {
                pageSize: 50,
                pageIndex: pageParam ? Math.max(0, Number(pageParam) - 1) : 0,
            },
            sorting: defaultSortCol ? [{ id: defaultSortCol, desc: defaultSortDirection === 'desc' }] : undefined,
            columnVisibility: initialColumnVisibility,
        },
        // size: 0 keeps columns fluid (auto) by default; set explicit size on columns to enable resizing
        defaultColumn: {
            size: 0,
            minSize: 0,
        },
    });
    const globalFilter = table.getState().globalFilter;

    useEffect(() => {
        const storage = localStorage.getItem(LOCAL_STORAGE_KEY);

        if (storage) {
            const { timeStamp, top, page } = JSON.parse(storage);
            // scroll table to position stored in localStorage if it was put there in the last 10 minutes
            if (
                differenceInMinutes(new Date(timeStamp), new Date()) < 10 &&
                page === table.getState().pagination.pageIndex
            ) {
                wrapperRef.current?.scrollTo({ top });
            }

            localStorage.removeItem(LOCAL_STORAGE_KEY);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        table.setGlobalFilter(searchParam);
    }, [searchParam, table]);

    useEffect(() => {
        onFilteredRowCountChange?.(table.getFilteredRowModel().rows.length);
    }, [onFilteredRowCountChange, table, globalFilter, data]);

    const onClick = (rowData: T) => {
        localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({
                timeStamp: Date.now(),
                top: wrapperRef.current?.scrollTop.toString() || '0',
                page: table.getState().pagination.pageIndex,
            }),
        );
        rowOnClick?.(rowData);
    };

    if (isLoading) {
        return <PageSpinner />;
    }

    return (
        <ErrorBoundary>
            {isUpdating && <UpdateAnimation />}
            {exportPortalId && (
                <Portal id={exportPortalId}>
                    <ExportDropdown table={table} exportName={exportName} />
                </Portal>
            )}
            <ScrollableWrapper ref={wrapperRef}>
                <Card className={cn('relative m-4', cardClassName)}>
                    {renderToolbar && toolbarPosition === 'overlay' && (
                        <div className="absolute top-3 right-3 z-10">{renderToolbar(table)}</div>
                    )}
                    {renderToolbar && toolbarPosition === 'header' && (
                        <div className="flex justify-end border-b px-6 py-3">{renderToolbar(table)}</div>
                    )}
                    <ContentWrapper $padding="0 24px 32px 24px" $gap={32}>
                        <DataTable>
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <HeaderRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <StyledDataTableHeaderCell
                                                key={header.id}
                                                style={{ width: header.getSize() || undefined }}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <HorizontalLayout
                                                        $alignItems="center"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <Text $uppercase $themeColor="subtle">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                        </Text>
                                                        {header.column.getCanSort() ? (
                                                            <SortArrow
                                                                $active={!!header.column.getIsSorted()}
                                                                $desc={header.column.getIsSorted() === 'desc'}
                                                                size="tiny"
                                                            />
                                                        ) : null}
                                                    </HorizontalLayout>
                                                )}
                                                {onColumnSizingChange &&
                                                    header.column.getCanResize() &&
                                                    !header.column.columnDef.meta?.isDisplayCol && (
                                                        <ResizeHandle
                                                            $isResizing={header.column.getIsResizing()}
                                                            onMouseDown={header.getResizeHandler()}
                                                            onTouchStart={header.getResizeHandler()}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    )}
                                            </StyledDataTableHeaderCell>
                                        ))}
                                    </HeaderRow>
                                ))}
                            </thead>
                            <tbody>
                                {[...table.getTopRows(), ...table.getCenterRows()].map((row) => (
                                    <DataTableRow
                                        key={row.id}
                                        {...(rowOnClick ? { onClick: () => onClick(row.original) } : {})}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <DataTableCell
                                                key={cell.id}
                                                $width={cell.column.getSize()}
                                                style={{ width: cell.column.getSize() || undefined }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </DataTableCell>
                                        ))}
                                    </DataTableRow>
                                ))}
                            </tbody>
                        </DataTable>
                        <Pagination table={table} />
                    </ContentWrapper>
                </Card>
            </ScrollableWrapper>
        </ErrorBoundary>
    );
};

const Pagination = <T extends object>({ table }: { table: Table<T> }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchParams, setSearchParams] = useSearchParams();

    const pageCount = table.getPageCount();
    const gotoPage = table.setPageIndex;
    const { pageIndex, pageSize } = table.getState().pagination;
    const totalRowCount = table.getPrePaginationRowModel().rows.length;

    useEffect(() => {
        setSearchParams(
            (urlSearchParams) => {
                urlSearchParams.set('page', (pageIndex + 1).toString());
                return urlSearchParams;
            },
            {
                replace: true,
            },
        );
    }, [pageIndex, setSearchParams]);

    return (
        <HorizontalLayout $stretch $apart $rowCenter>
            <Text>
                Showing {pageIndex * pageSize + 1} to{' '}
                {pageIndex + 1 === pageCount ? totalRowCount : pageSize * (pageIndex + 1)} of {totalRowCount} entries
            </Text>
            <HorizontalLayout $gap={16}>
                <HorizontalLayout $rowCenter>
                    {pageCount > 2 && (
                        <ArrowIconWrapper $left onClick={() => gotoPage(0)} $disabled={!table.getCanPreviousPage()}>
                            <IconDoubleChevron size="micro" />
                        </ArrowIconWrapper>
                    )}
                    <ArrowIconWrapper
                        $left
                        onClick={() => table.previousPage()}
                        $disabled={!table.getCanPreviousPage()}
                    >
                        <RightIconChevron size="micro" />
                    </ArrowIconWrapper>

                    <PageNumbers currentPage={pageIndex} pageCount={pageCount} gotoPage={gotoPage} />

                    <ArrowIconWrapper onClick={() => table.nextPage()} $disabled={!table.getCanNextPage()}>
                        <IconChevron size="micro" />
                    </ArrowIconWrapper>
                    {pageCount > 2 && (
                        <ArrowIconWrapper
                            $rotateRight
                            onClick={() => gotoPage(pageCount - 1)}
                            $disabled={!table.getCanNextPage()}
                        >
                            <IconDoubleChevron size="micro" />
                        </ArrowIconWrapper>
                    )}
                </HorizontalLayout>
                {pageCount > 4 && (
                    <Select
                        value={pageIndex + 1}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => gotoPage(Number(e.target.value) - 1)}
                    >
                        {Array.from({ length: pageCount }, (_, i) => {
                            const pageNumber = i + 1;
                            return (
                                <option key={pageNumber} value={pageNumber}>
                                    {pageNumber}
                                </option>
                            );
                        }).reverse()}
                    </Select>
                )}
            </HorizontalLayout>
            <HorizontalLayout $gap={16} $rowCenter>
                <Text>Show: </Text>
                <Select
                    value={pageSize}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => table.setPageSize(Number(e.target.value))}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            {size} rows
                        </option>
                    ))}
                </Select>
            </HorizontalLayout>
        </HorizontalLayout>
    );
};

const PageNumbers = ({
    currentPage,
    pageCount,
    gotoPage,
}: {
    currentPage: number;
    pageCount: number;
    gotoPage: (page: number) => void;
}) => {
    const start = Math.min(Math.max(currentPage - 1, 2), Math.max(pageCount - 3, 2));
    const end = Math.max(Math.min(3, pageCount - 2), 0) + start;

    const centralNumbers = Array.from({ length: end - start }, (_, i) => (
        <PageNumber key={i + start} $selected={currentPage === i + start - 1} onClick={() => gotoPage(i + start - 1)}>
            <Text>{i + start}</Text>
        </PageNumber>
    ));

    return (
        <>
            <PageNumber $selected={currentPage === 0} onClick={() => gotoPage(0)}>
                1
            </PageNumber>

            {currentPage > 3 && pageCount > 5 ? <PageNumberDots>...</PageNumberDots> : null}
            {centralNumbers}
            {currentPage < pageCount - 2 && pageCount > 5 ? <PageNumberDots>...</PageNumberDots> : null}

            {pageCount > 1 && (
                <PageNumber $selected={pageCount - 1 === currentPage} onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                </PageNumber>
            )}
        </>
    );
};

interface ExportProps<T> {
    table: Table<T>;
    exportName: string;
}

const ExportDropdown = <T extends object>(props: ExportProps<T>) => {
    return (
        <Dropdown
            placement="bottom-end"
            dropdownTrigger={
                <Button variant="outline2" size="sm" className="h-9 w-9 p-0">
                    <IconDots />
                </Button>
            }
        >
            <DropdownButtonGroup buttons={[{ button: 'Export .csv', onClick: () => exportTableToCsv(props) }]} />
        </Dropdown>
    );
};

const ScrollableWrapper = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
`;

const ContentWrapper = styled(VerticalLayout)`
    overflow: visible;
`;

const UpdateAnimation = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 6px;
    width: 100px;
    background: ${({ theme }) => theme.colors.cocoa900};
    animation: loading 1.5s ease-in-out infinite;

    @keyframes loading {
        0% {
            left: 0;
            width: 0;
        }
        50% {
            left: calc(100% - 100px);
            width: 100px;
        }
        100% {
            left: 0;
            width: 0;
        }
    }
`;
